import { put, takeLeading, call, select } from 'redux-saga/effects';
import { getStripe } from '../../../../../util/api/StripeApi';
import {
  getStripeRequest,
  getStripeResponse,
} from '../../../reducers/ducks/legacy/StripeDuck';
import * as orderActions from '../../../reducers/ducks/legacy/OrderDuck';
import orderTimingGuard from './orderTimingGuard';
import { getOrderTiming } from '../../../../../util/orderTiming';
import {
  deliveryCheckoutMatches,
  revisionParameter,
} from '../../../../../util/checkoutDeliveryAuthority';
import { createCheckoutAttemptContext } from '../../../../../util/checkoutContext';
import {
  beginCheckoutIssuance,
  clearCheckoutIssuance,
  readCheckoutIssuance,
  issuanceRecoveryMessage,
  retainIssuanceDiagnostic,
} from '../../../../../util/checkoutIssuanceRecovery';
import { readIssuedCheckout } from '../../../../../util/checkoutIssuedPayment';
import { checkoutStatusError } from '../../../../../util/checkoutDispatchFailure';

export function* getStripeSaga({ payload }) {
  if (yield select(readCheckoutIssuance)) return;
  if (yield select(readIssuedCheckout)) return;
  if (yield select((state) => state.order.checkoutData?.paymentIssued)) return;
  if (!(yield select(deliveryCheckoutMatches))) return;
  const context = createCheckoutAttemptContext(yield select((state) => state));
  const contextKey = yield select(context);
  const checkoutData = yield select((state) => state.order.checkoutData);
  const { showError, suppressErrorReport, ...requestPayload } = payload;
  const timing = yield select((state) => getOrderTiming(state));
  try {
    if (!(yield call(orderTimingGuard, true))) return;
    if (requestPayload.checkout !== checkoutData?.id) return;
    const request = { ...requestPayload, ...revisionParameter(checkoutData) };
    yield call(
      beginCheckoutIssuance,
      yield select((state) => state),
      request,
      'hosted'
    );
    const response = yield call(getStripe, request);
    if (
      (yield select(context)) !== contextKey ||
      (yield select((state) => state.order.checkoutData?.id)) !==
        checkoutData.id ||
      (yield select((state) => state.order.checkoutData?.delivery_revision)) !==
        checkoutData.delivery_revision
    )
      return;
    if (
      response.already_confirmed &&
      response.checkout_id === checkoutData.id
    ) {
      yield call(clearCheckoutIssuance);
      yield put(getStripeResponse({ response }));
      return;
    }
    if (
      checkoutData.delivery_revision &&
      (response.checkout_id !== checkoutData.id ||
        response.delivery_revision !== checkoutData.delivery_revision)
    )
      throw new Error('Hosted payment response does not match this checkout.');
    if (!response.redirection_url)
      throw new Error('Hosted payment URL is unavailable.');
    if (
      (yield select((state) => getOrderTiming(state).key)) !== timing.key ||
      !(yield call(orderTimingGuard, true))
    )
      return;
    yield put(
      orderActions.setCheckoutData({
        ...checkoutData,
        accountId: yield select((state) => state.main.user?.id),
        restaurant:
          checkoutData.restaurant ||
          (yield select((state) => state.restaurant.selectedRestaurantId)),
        paymentIssued: true,
      })
    );
    yield call(clearCheckoutIssuance);
    yield put(getStripeResponse({ response }));
  } catch (error) {
    if ((yield select(context)) !== contextKey) return;
    yield call(retainIssuanceDiagnostic, yield select((state) => state), error);
    const { message } = checkoutStatusError(error, issuanceRecoveryMessage);
    yield put(orderActions.setCheckoutDeliveryBinding(null));
    yield put(
      orderActions.setCheckoutDeliveryResult({ status: 'error', message })
    );
    if (typeof showError === 'function') showError(message);
    yield put(getStripeResponse({ error }));
  }
}

export function* watchStripeSagas() {
  yield takeLeading(getStripeRequest.type, getStripeSaga);
}
